import React from 'react'
import { ChunkLoadRecoveryPanel } from './ChunkLoadRecoveryPanel'
import { UnexpectedErrorFallback } from './UnexpectedErrorFallback'
import { autoRecoverFromChunkLoadError, isChunkLoadError } from 'helpers/chunkLoadRecovery'

interface ErrorBoundaryProps {
 children: React.ReactNode
 fallback?: React.ReactNode
}

interface ErrorBoundaryState {
 hasError: boolean
 error?: Error
 isChunkLoadError: boolean
 retryingSoft: boolean
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
 constructor(props: ErrorBoundaryProps) {
 super(props)
 this.state = { hasError: false, isChunkLoadError: false, retryingSoft: false }
 }

 static getDerivedStateFromError(error: Error): ErrorBoundaryState {
 return {
 hasError: true,
 error,
 isChunkLoadError: isChunkLoadError(error),
 retryingSoft: false,
 }
 }

 componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
 if (isChunkLoadError(error)) {
 void autoRecoverFromChunkLoadError()
 } else {
 console.error('ErrorBoundary caught:', error, errorInfo)
 }
 }

 render() {
 if (this.state.hasError) {
 if (this.state.isChunkLoadError) {
 return (
 <ChunkLoadRecoveryPanel
 isOffline={typeof navigator !== 'undefined' && !navigator.onLine}
 retryingSoft={this.state.retryingSoft}
 onSoftRetry={() => {
 this.setState({ retryingSoft: true })
 window.setTimeout(() => {
 this.setState({
 hasError: false,
 error: undefined,
 isChunkLoadError: false,
 retryingSoft: false,
 })
 }, 200)
 }}
 />
 )
 }
 if (this.props.fallback) return this.props.fallback
 return (
 <UnexpectedErrorFallback
 onRetry={() =>
 this.setState({ hasError: false, error: undefined, isChunkLoadError: false, retryingSoft: false })
 }
 />
 )
 }
 return this.props.children
 }
}
